import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DraggableDialogHeaderComponent } from '@dialog';
import { MessagingStore } from '../../../../../data-access/src/index';
import { Message } from '../../../../../util/src/index';
import { MessageBubbleComponent, SenderType } from '../../../../../components/ui/src/index';
import { MessageContentPipe } from '../../../../../components/pipes/src/index';

export interface SmsDialogData {
  patientId: number;
  patientCell: string;
}

const SMS_MAX_LENGTH = 130;

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'messaging-sms-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, DraggableDialogHeaderComponent, MessageContentPipe, MessageBubbleComponent],
  templateUrl: './sms-dialog.component.html',
  styleUrls: [
    '../../../../../../ui-components/src/lib/_styles/abstracts/_colors.scss',
    '../../../../../../ui-components/src/lib/_styles/abstracts/_typography.scss',
    '../../../../../../ui-components/src/lib/_styles/abstracts/_spacing.scss',
    './sms-dialog.component.scss',
  ],
  providers: [],
})
export class SmsDialogComponent implements OnInit {
  private readonly dialogRef = inject(DialogRef);
  protected readonly store = inject(MessagingStore);
  protected readonly data = inject<SmsDialogData>(DIALOG_DATA);
  protected message = '';
  protected hideSystemMessages = false;
  protected readonly sending = signal(false);
  protected readonly maxLength = SMS_MAX_LENGTH;
  protected readonly messages = computed(() => this.store.patientMessages());
  protected readonly hasMore = computed(() => this.store.hasMore());
  protected readonly isLoading = computed(() => this.store.isLoading());
  protected readonly charsLeft = computed(() => SMS_MAX_LENGTH - (this.message?.length ?? 0));
  readonly patientId = computed(() => this.store.activePatientId() || 0);

  ngOnInit() {
    this.store.openPatientMessages(this.patientId());
    this.store.startPolling();
  }

  protected sendMessage() {
    if (!this.message?.trim()) return;
    this.store.sendMessage({
      patientId: this.patientId(),
      content: `MESSAGE FROM NOREPLY@LEMONAID\n${this.message}`,
      entryType: 'sms',
      mobileNo: this.data.patientCell,
    });
    this.message = '';
  }

  protected markAsRead() {
    this.store.markPatientMessagesAsRead({ patientId: this.patientId() });
  }

  protected shouldHide(msg: Message): boolean {
    return this.hideSystemMessages && (msg.from.id === -1 || msg.from.firstname === null);
  }

  protected getSenderType(msg: Message): SenderType {
    if (msg.from.id === -1 || msg.from.firstname === null) return 'system';
    if (msg.from.id === this.patientId()) return 'patient';
    return 'doctor';
  }

  close() {
    this.dialogRef.close();
  }
}
